// Durable global Google Maps auth-failure guard.
//
// When the Google Maps JS API (or the Places Autocomplete widget) fails to
// authenticate — invalid key, key not authorised for this origin, Places API
// not enabled, or billing not configured — Google renders its default
// "This page can't load Google Maps correctly" dialog directly into the DOM
// (anchored under the input for Places Autocomplete). This popup is scary and
// unhelpful to end users; the address field should simply degrade to a plain
// text input.
//
// Google's documented escape hatch: if a global `window.gm_authFailure`
// function is defined at the moment auth fails, Google invokes it INSTEAD of
// showing the default error UI. The individual forms (CustomerForm,
// LocationForm, TerminalForm, …) already install per-form handlers, but they
// save/restore `prevAuthFailure`, and the shared `useGoogleMapsScript` hook
// restores it to `undefined` after the script loads. That leaves a window —
// exactly while the user is typing and the first Places request is validated —
// where no handler is defined, so Google falls back to its default popup.
//
// Installing a durable, process-lifetime handler at app bootstrap guarantees a
// defined `gm_authFailure` at all times. The per-form handlers chain to it via
// `prevAuthFailure`, so their cleanup still runs; this only removes the "no
// handler defined" gap. Purely additive — no existing form or hook logic
// changes.

/**
 * Remove any Google-injected auth-failure error UI from the DOM. Belt-and-
 * suspenders alongside suppressing the default dialog: if a broken
 * `.pac-container` was already rendered, tear it down so no stray error box
 * lingers under the address input. Safe to call repeatedly and safe when no
 * such nodes exist.
 */
function removeInjectedMapsErrorUi(): void {
  try {
    // Places Autocomplete renders its dropdown (and, on auth failure, the
    // error dialog) inside `.pac-container`. On a failed key it carries no
    // usable suggestions, so removing it is harmless.
    document.querySelectorAll('.pac-container').forEach((el) => el.remove());
  } catch {
    // ignore DOM access issues
  }
}

let installed = false;

/**
 * Install the durable global `gm_authFailure` handler exactly once. Idempotent:
 * repeated calls are no-ops. Called from `main.tsx` before React renders so a
 * handler is defined for the entire app lifetime.
 */
export function installGoogleMapsAuthGuard(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const w = window as unknown as { gm_authFailure?: () => void };
  const existing = w.gm_authFailure;

  w.gm_authFailure = () => {
    removeInjectedMapsErrorUi();
    // Preserve any handler that was somehow already registered before us.
    if (typeof existing === 'function') {
      try {
        existing();
      } catch {
        // never let a downstream handler throw out of gm_authFailure
      }
    }
  };
}
